import { ButtonProps } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';

import { AbsenceType } from '~/data/absences/types';
import { Enum } from '~/data/enums';
import {
  AbsenceForm,
  submitFormAbsence,
} from '~/features/AbsenceChanges/EditAbsenceDialog/data';
import { useAbsenceValidationSchema } from '~/features/AbsenceChanges/EditAbsenceDialog/useAbsenceValidationSchema';
import { useEditAbsenceDialogData } from '~/features/AbsenceChanges/EditAbsenceDialog/useEditAbsenceDialogData';

type PropsType = {
  employeeId: number;
};

type GetInstantAbsenceProps = Partial<AbsenceForm> & {
  absenceReason: Enum;
  days: number;
};

type GetInstantAbsenceOutput = {
  buttonProps: Pick<ButtonProps, 'disabled' | 'onClick' | 'title'>;
  error?: ValidationError;
};
type GetDateRangeByDaysOutput = Required<
  Pick<AbsenceForm, 'startDate' | 'endDate' | 'days'>
>;

type UseInstantAbsenceOutput = {
  reasonOptions: Enum[];
  isSubmitting: boolean;
  getInstantAbsence: (
    absenceProps: GetInstantAbsenceProps,
  ) => GetInstantAbsenceOutput;
  getDateRangeByDays: (
    startDate: Date,
    days: number,
  ) => GetDateRangeByDaysOutput;
  isLoading: boolean;
};

export const useInstantAbsences = (
  props: PropsType,
): UseInstantAbsenceOutput => {
  const { employeeId } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Form master states
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { formProps, isLoading } = useEditAbsenceDialogData({
    employeeId,
    absenceType: AbsenceType.SHORT,
  });
  const { reasonOptions, refreshAbsencesData } = formProps;

  const { validationSchema } = useAbsenceValidationSchema({
    formProps,
    yearOptions: [],
  });

  const handleSubmit = useCallback(
    (data?: AbsenceForm) => {
      if (!data) {
        return;
      }

      setIsSubmitting(true);

      // Process submission
      submitFormAbsence({
        employeeId,
        absenceType: AbsenceType.SHORT,
        absenceId: undefined,
        data,
      })
        .then(() => {
          enqueueSnackbar(t('successfullyAddedTheAbsence'), {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar(t('failedToAddAbsence'), {
            variant: 'error',
          });
        })
        .finally(() => {
          refreshAbsencesData();
          setIsSubmitting(false);
        });
    },
    [employeeId, enqueueSnackbar, t, refreshAbsencesData],
  );

  const validAbsenceData = useCallback(
    (data: AbsenceForm) => {
      try {
        return { data: validationSchema.validateSync(data) };
      } catch (err) {
        return { data: undefined, error: err as unknown as ValidationError };
      }
    },
    [validationSchema],
  );

  const getInstantAbsence = useCallback(
    (absenceProps: GetInstantAbsenceProps): GetInstantAbsenceOutput => {
      const { absenceReason, days, startDate, endDate } = absenceProps;

      const start = !!startDate ? moment(startDate) : moment();
      const end = !!endDate
        ? moment(endDate)
        : days > 1
        ? start.add(days - 1, 'day')
        : start;

      const data: AbsenceForm = {
        absenceReason,
        days,
        startDate: start.toDate(),
        endDate: end.toDate(),
        info: '',
        attachmentId: -1,
        attachmentName: '',
      };

      const { data: absenceData, error } = validAbsenceData(data);

      return {
        buttonProps: {
          disabled: !absenceData,
          onClick: () => {
            handleSubmit(absenceData);
          },
        },
        error,
      };
    },
    [validAbsenceData, handleSubmit],
  );

  const getDateRangeByDays = useCallback(
    (startDate: Date, days: number): GetDateRangeByDaysOutput => {
      return {
        startDate,
        endDate:
          days > 1
            ? moment(startDate)
                .add(days - 1, 'days')
                .toDate()
            : startDate,
        days,
      };
    },
    [],
  );

  return {
    reasonOptions,
    isSubmitting,
    getInstantAbsence,
    getDateRangeByDays,
    isLoading,
  };
};
